import * as React from 'react';
import { motion, useAnimation, useInView } from 'framer-motion';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '@util/functions/utils';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-10 w-full items-center justify-between rounded-md border border-neutral-200 bg-white px-1.5 py-2 ring-offset-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-neutral-950 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-5',
      className
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 min-w-[8rem] overflow-hidden rounded-md border border-neutral-200 bg-white text-neutral-950 shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
        'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className
      )}
      position={position}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
          'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('py-1.5 pl-2 pr-2 text-sm font-semibold', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-[4px] py-1.5 pl-2 pr-2 text-sm outline-none focus:bg-neutral-100 focus:text-neutral-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 ',
      className
    )}
    {...props}
  >

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-neutral-100', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export interface ISelectInputProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  options: string[] | Array<{ text: string, value: string }>
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  value: string
  placeholder: string
  name: string
  className?: string
  error?: boolean
  errorMessage?: string
}

export const SelectInput: React.FC<ISelectInputProps> = ({
  options,
  onChange,
  value,
  placeholder,
  name,
  className,
  error,
  errorMessage,
  ...props
}) => {
  const handleChange = (value: string): void => {
    const e = {
      target: {
        value,
        name
      }
    };
    onChange(e as unknown as React.ChangeEvent<HTMLSelectElement>);
  };
  const ref = React.useRef<HTMLDivElement>(null);
  const isInVIew = useInView(ref);
  const mainControls = useAnimation();
  React.useEffect(() => {
    if (isInVIew) {
      void mainControls.start("visible");
    } else {
      void mainControls.start("hidden");
    }
  }, [isInVIew, mainControls]);
  return (
    <motion.span
      ref={ref}
      variants={{
        hidden: {
          opacity: 0,
          scale: 0.2,
        },
        visible: {
          opacity: 1,
          scale: 1,
        },
      }}
      initial="hidden"
      animate={mainControls}
      transition={{
        ease: [0.6, 0.01, 0.05, 0.95],
        duration: 1.25,
      }}
      className="flex flex-col space-y-2 w-full">
      <Select
        onValueChange={handleChange}
      >
        <SelectTrigger
          className={cn(`bg-white border-animation data-details:text-secondary-425/50 appearance-none rounded-lg w-full data-[true="true"]:ring-primary-890 ${value?.length < 1 ? 'text-[#7C7C7C]' : 'text-dark'} text-sm outline-none focus-within:ring-[#C4C4C4]  focus-within:data-true:ring-primary-890  data-true:ring-1 focus-within:ring-1 focus-within:outline-none focus:outline-none focus-visible:outline-none transition-all duration-300 ease-in pl-2`, className)}
          data-true={error}
          data-details={value?.length < 1}
        >
          <SelectValue
            className={`text-sm data-[true="true"]:ring-primary-890 ${value?.length < 1 ? 'text-[#7C7C7C]' : 'text-dark'}  w-full pl-2'`}
            placeholder={placeholder}
            defaultValue={value ?? ''}
            data-true={error}
            data-details={value?.length < 1}
          />
        </SelectTrigger>
        <SelectContent className="max-h-72">
          <SelectGroup>
            <SelectLabel>{placeholder}</SelectLabel>
            {options?.map((val: string | { text: string, value: string }) => (
              <SelectItem className="text-xs" key={typeof val === 'object' ? val?.value : val} value={`${typeof val === 'object' ? val?.value : val}`}>
                {typeof val === 'object' ? val?.text : val}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      {(error ?? false) && (
        <span className="text-primary-890 w-fit ml-auto">{errorMessage}</span>
      )}
    </motion.span>
  );
};
export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator
};
